import { Box } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { useAddressRecord } from 'src/graphql';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { SiteWindow } from 'src/pages/Sites/site';
import { useRegisterConversationModalDefaults } from 'src/pages/Sites/use-register-conversation-modal-defaults';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { TabType, useGlobalTabs, useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';

export const SiteExpanded = () => {
  const { showErrorAlert } = useNotifications();
  const { isMobile } = useScreenDimensions();
  const location = useLocation();
  const { navigate } = useNavigateRoute();
  const { recordId } = useRouteParams(UrlParams.recordId);
  const { openInTab } = useGlobalTabs();
  const { address, isLoading } = useAddressRecord({
    addressId: recordId,
  });

  const currentPartnerId = useGlobalStore.use.selectedPartnerId();

  const { setModalProps: setAddProjectModalProps } = useModalMethods(Modals.AddProject);

  useRegisterConversationModalDefaults(address);

  const customViewId = (location.state as { customViewId?: string })?.customViewId;

  useEffect(() => {
    let deleteAddProjectModalProps: (() => void) | undefined;
    if (currentPartnerId && address) {
      deleteAddProjectModalProps = setAddProjectModalProps({
        address,
      });
    }
    return () => {
      deleteAddProjectModalProps?.();
    };
  }, [currentPartnerId, setAddProjectModalProps, address]);

  const { customViews } = useCustomViews({
    entity: EntityNames.Site,
  });

  const customView = useMemo(
    () => (customViewId ? customViews.find((i) => i.customViewId === customViewId) : null),
    [customViews, customViewId],
  );

  // open expanded site in tab.
  useEffect(() => {
    if (!isLoading) {
      if (address) {
        openInTab(
          { type: TabType.Site, entity: address },
          { customViewId: customView?.customViewId },
        );
      } else {
        showErrorAlert(`Site "${recordId}" not found. You've been redirected to All Sites.`);
        navigate(
          portalPaths.entity.all({
            entityName: 'sites',
            customViewId: customView?.customViewId,
          }),
        );
      }
    }
  }, [
    customViewId,
    openInTab,
    isLoading,
    address,
    customView?.customViewId,
    showErrorAlert,
    recordId,
    navigate,
  ]);

  const onCloseSite = useCallback(() => {
    if (customView?.customViewId) {
      navigate(
        portalPaths.entity.selected(
          customView.entityName,
          customView.customViewId,
          !isMobile ? recordId : undefined,
        ),
      );
    } else {
      navigate(
        portalPaths.entity.all({
          entityName: 'site',
          id: isMobile ? recordId : '',
        }),
      );
    }
  }, [customView?.customViewId, customView?.entityName, isMobile, navigate, recordId]);

  return address ? (
    <MetaTags title={address.fullAddress}>
      <Box height="100%" position="relative">
        <EntityWindowWrapper>
          {isMobile ? (
            <SiteSidePanel site={address} />
          ) : (
            <SiteWindow isSidePanelExpanded site={address} onCloseSite={onCloseSite} />
          )}
        </EntityWindowWrapper>
      </Box>
    </MetaTags>
  ) : null;
};
